import { FC, memo, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import ChannelListPopup from '~/src/components/Bundles/ChannelListPopup';
import { FiltersForTariffs } from '~/src/components/FiltersForTariffs';
import TariffLine, { ESortingKeys } from '~/src/components/TariffLine';
import { ITariff } from '~/src/entities/formattedTariff';
import { ITariffLine } from '~/src/entities/tariff-lines';
import TariffLineCard, { IContentsItem } from '~/src/features/tariffLines/Card';
import CardSwiper from '~/src/features/tariffLines/CardSwiper';
import TariffLineHeader from '~/src/features/tariffLines/header';
import { getTariffsReduceByIsCottage } from '~/src/features/tariffLines/helpers';
import SameTariffLineCard, { IRecommendedTariff } from '~/src/features/tariffLines/SameCard';

import PageSection from './Section';

import * as ST from './styled';

interface IProps {
    tariffLine: ITariffLine,
}

const filterItems = [
    {
        id: 'bundles',
        checked: true,
        disabled: false,
        label: 'С телевидением',
    },
    {
        id: 'internet',
        checked: false,
        disabled: false,
        label: 'Только интернет',
    },
];

type TariffQuery = Partial<{
    filtersCottage: string,
    monoTariff: string,
}>

const TariffLines: FC<IProps> = ({ tariffLine }): JSX.Element => {
    const { query: { filtersCottage, monoTariff } } = useRouter<TariffQuery>();

    const {
        initialSwitcherState,
        initialFiltersState,
        initialActiveFilter,
    } = useMemo(() => {
        const initialIsInternetActive = monoTariff === 'true';
        const filtersState = initialIsInternetActive
            ? filterItems.map(({ checked, ...filter }) => ({
                ...filter,
                checked: !checked,
            }))
            : filterItems;

        return {
            initialFiltersState: filtersState,
            initialSwitcherState: filtersCottage === 'true' ? 1 : 0,
            initialActiveFilter: initialIsInternetActive ? 'internet' : 'bundles',
        };
    }, [filtersCottage, monoTariff]);

    const [activeSwitcher, setActiveSwitcher] = useState(initialSwitcherState);
    const [filters, setFilters] = useState(initialFiltersState);
    const [activeFilter, setActiveFilter] = useState<string | null>(initialActiveFilter);

    const [isIncludesPopupOpen, setIsIncludesPopupOpen] = useState(false);
    const [activePopupContent, setActivePopupContent] = useState<IContentsItem>();
    const [isChannelPopupOpen, setIsChannelPopupOpen] = useState(false);
    const [selectedTariff, setSelectedTariff] = useState<ITariff | null>(null);

    const tariffRef = useRef<HTMLDivElement>(null);

    const menuItems = [
        {
            text: 'Для квартиры',
            action: () => {
                setActiveSwitcher(0);
            },
        },
        {
            text: 'Для частного дома',
            action: () => {
                setActiveSwitcher(1);
            },
        },
    ];

    const tariffs = useMemo(() => getTariffsReduceByIsCottage(tariffLine.tariffs), [tariffLine]);

    const handleIncludesPopupClose = () => {
        setIsIncludesPopupOpen(false);
    };

    const handleTariffClick = () => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffLines,
            action: 'click choose tariff',
            label: '',
        });
        tariffRef?.current?.scrollIntoView();
        setIsIncludesPopupOpen(false);
    };

    const handleIncludesClick = (id: number) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffLines,
            action: 'click details additionally',
            label: '',
        });
        setIsIncludesPopupOpen(true);
        setActivePopupContent(tariffLine.contents?.find((el: IContentsItem) => el.id === id));
    };

    const filteredTariffs = (() => {
        const currentTariffs = activeSwitcher ? tariffs.cottage : tariffs.notCottage;

        if (activeFilter === null) {
            return currentTariffs.common;
        }

        return activeFilter === 'bundles' && tariffs.hasBundles ? currentTariffs.bundles : currentTariffs.mono;
    })();

    const toggleFilter = (filterId: string) => {
        setActiveFilter(currentActive => currentActive === filterId ? null : filterId);

        setFilters(currentFilters => currentFilters.map(filter => {
            let stateCheckedForFilter = filter.checked;

            if (filter.checked && filter.id !== filterId) {
                stateCheckedForFilter = false;
                dataLayerPush({
                    event: 'UAevent',
                    category: CATEGORIES.tariffCards,
                    action: `remove filter ${filter.id}`,
                    label: filter.label,
                });
            }

            if (filter.id === filterId) {
                stateCheckedForFilter = !filter.checked;
                dataLayerPush({
                    event: 'UAevent',
                    category: CATEGORIES.tariffCards,
                    action: `${stateCheckedForFilter ? 'add' : 'remove'} filter ${filter.id}`,
                    label: filter.label,
                });
            }

            return { ...filter, checked: stateCheckedForFilter };
        }));
    };

    const onChannelsClickHandler = (tariff: ITariff) => {
        setIsChannelPopupOpen(true);
        setSelectedTariff(tariff);
        dataLayerPush({
            event: 'UAevent',
            category: 'Click Bundles on the channel',
            action: 'view channels',
            label: '',
        });
    };

    const onCloseChannelsPopup = () => {
        setIsChannelPopupOpen(false);
        setSelectedTariff(null);
    };

    return (
        <>
            <TariffLineHeader scrollRef={tariffRef} tariffLine={tariffLine}/>
            <ST.Wrapper>
                <ST.Container>
                    {tariffLine.title_body &&
                        <PageSection
                            title={tariffLine.title_body}
                            description={tariffLine.body_full}
                        />
                    }
                    <PageSection
                        sectionRef={tariffRef}
                        title={tariffLine.title_tariffs}
                        description={tariffLine.body_tariffs}
                    >
                        <FiltersForTariffs
                            activeItem={activeSwitcher}
                            tariffSwitcherData={menuItems}
                            tariffFiltersData={tariffs.hasBundles ? filters : []}
                            onToggleFilter={toggleFilter}
                            hasCityTariffs={true}
                            hasVillageTariffs={true}
                        />
                        {filteredTariffs.length > 0
                            ? <TariffLine
                                baseTariffs={filteredTariffs}
                                sortField={ESortingKeys.INTERNET_LINES}
                                openPopup={onChannelsClickHandler}
                                isAuth={false}
                            />
                            : <ST.STariffCardPlug/>
                        }
                    </PageSection>
                    {!!tariffLine.recommendedTariffLines?.length &&
                        <PageSection
                            title={'Другие тарифы'}
                        >
                            <ST.Grid>
                                <CardSwiper slideWidth={'356px'}>
                                    {tariffLine.recommendedTariffLines.map((tariff: IRecommendedTariff) =>
                                        <SameTariffLineCard
                                            key={tariff.id}
                                            tariff={tariff}
                                        />)
                                    }
                                </CardSwiper>
                            </ST.Grid>
                        </PageSection>
                    }
                    {!!tariffLine.contents?.length &&
                        <PageSection
                            title={tariffLine.title_includes}
                            description={tariffLine.body_includes}
                        >
                            <ST.Grid>
                                <CardSwiper slideWidth={'332px'}>
                                    {tariffLine.contents.map((item: IContentsItem) =>
                                        <TariffLineCard
                                            key={item.id}
                                            onBtnClick={() => handleIncludesClick(item.id)}
                                            item={item}
                                        />)
                                    }
                                </CardSwiper>
                            </ST.Grid>
                        </PageSection>
                    }
                </ST.Container>
                {activePopupContent &&
                    <ST.IncludesPopup
                        isOpen={isIncludesPopupOpen}
                        fullWidth={false}
                        width={600}
                        onClose={handleIncludesPopupClose}
                    >
                        <div>
                            <WebpImage urlWebp={activePopupContent.image_webp}>
                                <ST.ImageBlock src={activePopupContent.image}/>
                            </WebpImage>
                        </div>
                        <ST.PopupInner>
                            <ST.PopupTitle>{activePopupContent.title}</ST.PopupTitle>
                            <div>
                                <ST.PopupDescription dangerouslySetInnerHTML={{ __html: activePopupContent.body }}/>
                            </div>
                            <ST.PopupButtonBlock>
                                <Button onClick={handleTariffClick}>
                                    Выбрать тариф
                                </Button>
                            </ST.PopupButtonBlock>
                        </ST.PopupInner>
                    </ST.IncludesPopup>
                }
            </ST.Wrapper>
            <ChannelListPopup
                isOpen={isChannelPopupOpen}
                onClose={onCloseChannelsPopup}
                freePackets={selectedTariff?.freePackets}
            />
        </>
    );
};

export default memo(TariffLines);
